import {Component, Input, OnInit, ViewChild, OnChanges} from '@angular/core';
import {
  ChartComponent,
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexStroke,
  ApexMarkers,
  ApexYAxis,
  ApexGrid,
  ApexTitleSubtitle,
  ApexLegend
} from "ng-apexcharts";
import {UtilService} from "../../services/util.service";

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  stroke: ApexStroke;
  dataLabels: ApexDataLabels;
  markers: ApexMarkers;
  colors: string[];
  yaxis: ApexYAxis;
  grid: ApexGrid;
  legend: ApexLegend;
  title: ApexTitleSubtitle;
  labels: string[]
};

@Component({
  selector: 'app-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.scss']
})
export class LineChartComponent implements OnInit {

  @Input() graphData: any = [];
  @Input() xAxisLabel: string = '';
  @Input() yAxisLabel: string = '';
  @Input() xAxisType: any = '';
  @Input() loading: boolean = false;
  @ViewChild("chart") chart: ChartComponent;
  public chartOptions: Partial<ChartOptions>;
  mockData = [
    {
      name: "Power",
      data: [10, 29, 8, 20, 32, 32, 33]
    },
    {
      name: "Gas",
      data: [15, 11, 14, 2, 17, 35]
    }
  ];

  constructor(private utilService: UtilService) {
    // @ts-ignore
    this.chartOptions = {
      series: [],
      chart: {
        height: "420",
        type: "line",
        stacked: false,
        dropShadow: {
          enabled: true,
          color: "#000",
          top: 18,
          left: 7,
          blur: 10,
          opacity: 0.2
        },
        toolbar: {
          show: false,
        }
      },
      colors: ["#77B6EA", "#545454"],
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: "smooth",
        width: 1
      },
      // labels: [],
      title: {
        text: " ",
        align: "center",
        style: {
          fontSize: '16px',
          fontWeight: 600
        }
      },
      grid: {
        borderColor: "#e7e7e7",
        row: {
          colors: ["#f3f3f3", "transparent"], // takes an array which will be repeated on columns
          opacity: 0.5
        }
      },
      markers: {
        size: 5
      },
      xaxis: {
        categories: [],
        title: {
          text: "Start Date",
          style: {
            fontSize: "16px",
            fontWeight: 600
          }
        }
      },
      yaxis: {
        title: {
          text: "Price ($ / MWh)",
          style: {
            fontSize: "16px",
            fontWeight: 600,
            cssClass: 'line-chart-y-title'
          }
        },
        decimalsInFloat: 0
      },
      legend: {
        position: "top",
        horizontalAlign: "center",
        floating: false,
        offsetY: 0,
        offsetX: -5,
        markers: {
          width: 30,
          height: 12,
          radius: 1
        }
      }
    };
  }

  ngOnInit(): void {
    this.initializeGraph();
  }

  ngOnChanges(data: any) {
    const {graphData: currentValue} = data;
    if(currentValue)
      this.initializeGraph();
  }

  initializeGraph(){
    if(this.graphData && this.graphData.series) {
      let chartOptions = JSON.parse(JSON.stringify(this.chartOptions));
      // let series = JSON.parse(JSON.stringify(this.graphData.series));
      // series = series.map((item, ind) => {
      //   item.name = `${ind + 1} ${item.name}`;
      //   return item;
      // })
      chartOptions.series = this.graphData.series;
      // if(this.xAxisType === 'datetime'){
      //   chartOptions.labels = this.graphData.xaxis;
      // }else{
        chartOptions.xaxis.categories = this.graphData.xaxis;
      // }
      chartOptions.xaxis.title.text = this.xAxisLabel;
      chartOptions.yaxis.title.text = this.yAxisLabel;
      chartOptions.colors = [];
      if(chartOptions.series.length){
        chartOptions.colors = chartOptions.series.map(item => this.utilService.getRandomColor());
      }
      if(this.xAxisType) {
        chartOptions.markers = {
          size: 0
        };
        const points = this.getAllNodePoints(this.graphData);
        // chartOptions.yaxis.labels = {
        //   formatter: val => Math.ceil(val/10) * 10
        // };
        chartOptions.yaxis.min = 10;
        chartOptions.yaxis.max = Math.ceil(points[points.length - 1]/10) * 10;
        chartOptions.yaxis.tickAmount = (chartOptions.yaxis.max/10) - 1;
        chartOptions.xaxis = {
          type: this.xAxisType,
          title: {
            text: this.xAxisLabel,
            style: {
              fontSize: "16px",
              fontWeight: 600,
              cssClass: 'line-chart-x-title'
            }
          }
        };
        chartOptions.chart = {
          ...chartOptions.chart,
          ...{
            toolbar: {
              show: true,
              autoSelected: "zoom",
              tools: {
                download: false
              }
            },
            zoom: {
              type: 'x',
              enabled: true,
              // autoScaleYaxis: true
            },
            events: {
              beforeZoom: (chartContext, {xaxis, yaxis}) => {
                const dates= this.getAllUniqueDates(this.graphData);
                const sDate = dates[0];
                const eDate = dates[dates.length - 1];
                if(xaxis.min <= sDate){
                  return {
                    xaxis: {
                      min: sDate,
                      max: eDate
                    },
                    yaxis: yaxis
                  }
                }else if(xaxis.max - xaxis.min <= eDate - this.getLastWeekDate(eDate)){
                  return {
                    xaxis: {
                      min: this.getLastWeekDate(eDate),
                      max: eDate
                    }
                  }
                }else{
                  return {
                    xaxis: {
                      min: xaxis.min,
                      max: eDate
                    }
                  }
                }
              }
            }
          }
        }
      }

      this.chartOptions = chartOptions;
    }else{
      this.chartOptions.series = [];
    }
  }

  getLastWeekDate(date){
    const dateObj = new Date(date);
    return new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate() - 7).getTime()
  }

  getAllUniqueDates(data){
    let dates = [];
    if(data.series.length){
      data.series.forEach(series => {
        if(series.data && series.data.length){
          series.data.forEach(item => {
            if(!dates.includes(item[0]))
              dates.push(item[0])
          })
        }
      })
    }
    return dates.sort((a, b) => a-b);
  }

  getAllNodePoints(data){
    let points = [];
    if(data.series.length){
      data.series.forEach(series => {
        if(series.data && series.data.length){
          series.data.forEach(item => {
            if(!points.includes(Math.round(item[1])))
              points.push(Math.round(item[1]))
          })
        }
      })
    }
    return points.sort((a, b) => a-b);
  }

}
